import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';

export const useReservationStore = defineStore('reservation', () => {
    const getFormattedDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // const startDateFormatted = getFormattedDate(new Date());
    // const endDateFormatted = getFormattedDate(new Date(new Date().setDate(new Date().getDate() + 1)));

    const now = new Date();
    const startDate = new Date(now);

    // 날짜 결정: 오후(>=12)면 +2일, 오전(<12)면 +1일
    if (now.getHours() >= 12) {
        startDate.setDate(startDate.getDate() + 1);
    } else {
        startDate.setDate(startDate.getDate());
    }

    // 시간 계산 & 30분 단위 반올림
    let startHour, startMinute;
    if (now.getHours() >= 12) {
        // 오후 이후는 고정 09:00
        startHour = 9;
        startMinute = 0;
    } else {
        // 오전이면 현재 +3시간
        const future = new Date(now.getTime() + 3 * 60 * 60 * 1000);
        startHour = future.getHours();

        // rawMinutes → 30분 단위로 반올림
        const rawMinutes = future.getMinutes();
        let rounded = Math.round(rawMinutes / 30) * 30;
        if (rounded === 60) {
            startHour += 1;
            rounded = 0;
        }
        startMinute = rounded;

        // 최소 08:00 보장
        if (startHour < 8) {
            startHour = 8;
            startMinute = 0;
        }
    }

    const startTimeFormatted = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`;
    const startDateFormatted = getFormattedDate(startDate);

    // 종료: 시작일 기준 +1일, 시간은 동일
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    const endDateFormatted = getFormattedDate(endDate);
    const endTimeFormatted = startTimeFormatted;

    /** @type {ReservationState} */
    const initialState = {
        email: '',
        customer_login_id: '',
        customer_name: '',
        birthdate: '',
        phone: '',
        customer_type: 'INDV',
        corporation_name: '',
        corporation_manager: '',
        corporation_manager_contact: '',
        business_no: '',
        corporation_address: '',
        corporation_address_detail: '',
        driver_name: '',
        driver_phone: '',
        driver_birth_date: '',
        driver_address: '',
        driver_address_detail: '',
        license_no: '',
        license_type: '',
        license_validity: 'N',
        is_second_driver: false,
        second_driver_name: '',
        second_driver_phone: '',
        second_driver_birth_date: '',
        second_driver_address: '',
        second_driver_address_detail: '',
        second_license_no: '',
        second_license_type: '',
        second_license_validity: 'N',
        vehicle_group_id: 0,
        vehicle_group_name: '',
        vehicle_group_image: '',
        original_price: 0,
        discount_price: 0,
        collision_insurance_excess: 0,
        total_price: 0,
        insurance_age: '',
        collision_insurance_state: 'NON',
        start_date: startDateFormatted,
        start_time: startTimeFormatted,
        end_date: endDateFormatted,
        end_time: endTimeFormatted,
        delivery_state: 'N',
        garage_id: 0,
        garage_name: '',
        pickup_delivery_id: 0,
        return_delivery_id: 0,
        pickup_address: '',
        pickup_address_detail: '',
        return_address: '',
        return_address_detail: ''
    };

    function resetCustomerAndDriverData() {
        const subset = {
            email: '',
            customer_login_id: '',
            customer_name: '',
            birthdate: '',
            phone: '',
            customer_type: 'INDV',
            corporation_name: '',
            corporation_manager: '',
            corporation_manager_contact: '',
            business_no: '',
            corporation_address: '',
            corporation_address_detail: '',
            driver_name: '',
            driver_phone: '',
            driver_birth_date: '',
            driver_address: '',
            driver_address_detail: '',
            license_no: '',
            license_type: '',
            license_validity: 'N',
            is_second_driver: false,
            second_driver_name: '',
            second_driver_phone: '',
            second_driver_birth_date: '',
            second_driver_address: '',
            second_driver_address_detail: '',
            second_license_no: '',
            second_license_type: '',
            second_license_validity: 'N'
        };
        Object.assign(reservationData, subset);
    }

    // state (reactive 객체 사용)
    const reservationData = reactive({ ...initialState });

    /**
     * 예약 데이터를 업데이트합니다.
     * @param {Partial<ReservationState>} newData 업데이트할 데이터 객체.  `ReservationState`의 일부 속성만 포함할 수 있습니다.
     */
    function updateReservationData(newData) {
        Object.assign(reservationData, newData);
    }

    /**
     * 예약 데이터 전체를 새로운 객체로 설정합니다.
     * @param {ReservationState} newData 새로운 예약 데이터 객체. `ReservationState`의 모든 속성을 포함해야 합니다.
     */
    function setReservationData(newData) {
        if (typeof newData !== 'object' || newData === null) {
            console.error('setReservationData: newData must be a valid object.');
            return;
        }
        // Object.keys(initialState).forEach(key => {
        //     if (!(key in newData)) {
        //         console.warn(`setReservationData: Missing property "${key}" in newData.`);
        //     }
        // });

        // reservationData의 모든 속성을 순회하며 newData의 값으로 설정
        Object.assign(reservationData, newData);
        // for (const key in reservationData) {
        //     if (newData.hasOwnProperty(key)) {
        //         reservationData[key] = newData[key];
        //     }
        // }
    }

    /**
     * 예약 데이터를 초기 상태로 재설정합니다.
     */
    function resetReservationData() {
        Object.assign(reservationData, JSON.parse(JSON.stringify(initialState))); // Object.assign(reservationData, initialState);
    }

    // const fullName = computed(() => {
    //   return `${reservationData.customer_name} ${reservationData.driver_name}`;
    // });

    const getReservationPayload = computed(() => {
        return {
            ...reservationData,
            start_date_time: reservationData.start_date ? `${reservationData.start_date}T${reservationData.start_time}:00` : '',
            end_date_time: reservationData.end_date ? `${reservationData.end_date}T${reservationData.end_time}:00` : '',
            start_date: undefined,
            start_time: undefined,
            end_date: undefined,
            end_time: undefined
        };
    });

    const rentalDuration = computed(() => {
        const start_dateTime = new Date(`${reservationData.start_date}T${reservationData.start_time}:00`);
        const end_dateTime = new Date(`${reservationData.end_date}T${reservationData.end_time}:00`);

        if (isNaN(start_dateTime) || isNaN(end_dateTime)) return '대여일, 반납일을 선택하세요.';

        const diffMs = end_dateTime - start_dateTime;

        if (diffMs < 0) return '0일 0시간 0분';

        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        return `${diffDays}일 ${diffHours}시간 ${diffMinutes}분`;
    });

    return { reservationData, updateReservationData, setReservationData, resetReservationData, resetCustomerAndDriverData, getReservationPayload, rentalDuration };
});
